"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { GridContainer } from "@/components/layout/GridContainer";

// --- Inline icons ---

const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28a1 1 0 0 0-1.5.86Z"
      fill="currentColor"
    />
  </svg>
);

// --- Types ---

type VideoShortItem = {
  playback_id: string;
  title?: string;
  duration?: string;
  thumbnail?: { filename: string; alt?: string; [k: string]: any };
  _uid: string;
  component: "video_short";
  [k: string]: any;
};

type VideoShortsGalleryStoryblok = {
  title?: string;
  items?: VideoShortItem[];
  _uid: string;
  component: "video_shorts_gallery";
  [k: string]: any;
};

type VideoShortsGalleryProps = {
  blok: VideoShortsGalleryStoryblok;
};

// --- Preload helpers ---

let muxPlayerPreloaded = false;
const preloadMuxPlayerBundle = () => {
  if (muxPlayerPreloaded) return;
  muxPlayerPreloaded = true;
  import("@mux/mux-player-react").catch(() => {});
};

const prefetchedManifests = new Set<string>();
const prefetchHlsManifest = (playbackId: string) => {
  if (prefetchedManifests.has(playbackId)) return;
  prefetchedManifests.add(playbackId);
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.href = `https://stream.mux.com/${playbackId}.m3u8`;
  link.as = "fetch";
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
};

// --- ShortsCard ---

type ShortsCardProps = {
  item: VideoShortItem;
  isActive: boolean;
  canHover: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick: () => void;
};

const ShortsCard = ({
  item,
  isActive,
  canHover,
  onHover,
  onLeave,
  onClick,
}: ShortsCardProps) => {
  const thumbnailUrl =
    item.thumbnail?.filename ||
    `https://image.mux.com/${item.playback_id}/thumbnail.jpg?width=360&height=640&fit_mode=smartcrop`;

  const animatedUrl = `https://image.mux.com/${item.playback_id}/animated.webp?width=360&height=640&fit_mode=smartcrop&fps=15`;

  return (
    <div
      className="relative h-[427px] w-[240px] overflow-hidden rounded-2xl cursor-pointer group"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      {/* Poster thumbnail */}
      <Image
        src={thumbnailUrl}
        alt={item.title || ""}
        fill
        sizes="180px"
        className="object-cover"
      />

      {/* Animated preview — preloaded on hover-capable devices, shown on hover */}
      {canHover && (
        <Image
          src={animatedUrl}
          alt=""
          fill
          sizes="180px"
          unoptimized
          className={`object-cover transition-opacity duration-200 ${isActive ? "opacity-100" : "opacity-0"}`}
        />
      )}

      {/* Title overlay with sun-100 highlight — hidden when playing */}
      {item.title && (
        <div
          className={`absolute left-4 right-4 top-1/2 -translate-y-1/2 text-center transition-opacity duration-300 ${
            isActive ? "opacity-0" : "opacity-100"
          }`}
        >
          <span className="text-headline  text-black leading-snug box-decoration-clone bg-sun-100 px-1 py-0.5">
            {item.title}
          </span>
        </div>
      )}

      {/* Bottom bar: play icon + duration — hidden when playing */}
      <div
        className={`absolute bottom-2 left-2 right-2 flex items-center gap-1 transition-opacity duration-300 ${
          isActive ? "opacity-0" : "opacity-100"
        }`}
      >
        <PlayIcon />
        {item.duration && (
          <span className="text-copy text-black">{item.duration}</span>
        )}
      </div>
    </div>
  );
};

// --- VideoShortsGallery ---

const useCanHover = () => {
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(hover: hover)");
    setCanHover(mql.matches);
    const handler = (e: MediaQueryListEvent) => setCanHover(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return canHover;
};

const VideoShortsGallery = ({ blok }: VideoShortsGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const canHover = useCanHover();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const items = blok.items || [];

  // Check if carousel can scroll
  const checkCanScroll = useCallback(() => {
    if (!api) return;
    setCanScroll(api.canScrollNext() || api.canScrollPrev());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    checkCanScroll();
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);
    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  // Track viewport visibility & preload MuxPlayer bundle
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting) preloadMuxPlayerBundle();
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Control autoplay based on canScroll and viewport visibility
  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  if (items.length === 0) return null;

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
      <GridContainer className="!px-0 md:py-8">
        <section className="col-span-12 bg-sand-100 rounded-3xl py-4 flex flex-col gap-4 overflow-hidden">
          {/* Title */}
          {blok.title && (
            <h2 className="text-headline-md text-center text-black px-6 mb-2">
              {blok.title}
            </h2>
          )}

          {/* Carousel */}
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: false,
              dragFree: false,
              watchResize: true,
              breakpoints: {
                "(min-width: 768px)": {
                  align: "start",
                  dragFree: true,
                },
              },
            }}
            plugins={[autoplayRef.current]}
            className="w-full relative"
          >
            <GalleryNavPrevious show={canScroll} />
            <CarouselContent
              className={
                canScroll
                  ? "ml-0 pl-6 items-stretch"
                  : "ml-0 pl-6 justify-center items-stretch"
              }
            >
              {items.map((item, index) => (
                <CarouselItem
                  key={item._uid}
                  className={
                    canScroll
                      ? "pl-4 basis-auto h-auto"
                      : "basis-auto px-2 h-auto"
                  }
                >
                  <ShortsCard
                    item={item}
                    isActive={activeIndex === index}
                    canHover={canHover}
                    onHover={() => {
                      setActiveIndex(index);
                      prefetchHlsManifest(item.playback_id);
                    }}
                    onLeave={() => setActiveIndex(null)}
                    onClick={() => {
                      const el = document.createElement("div");
                      el.setAttribute("data-lightbox-video", item.playback_id);
                      el.setAttribute("data-lightbox-alt", item.title || "");
                      el.setAttribute("data-lightbox-group", blok._uid);

                      const groupData = items.map((i) => ({
                        videoId: i.playback_id,
                        alt: i.title || "",
                      }));
                      el.setAttribute(
                        "data-lightbox-group-data",
                        JSON.stringify(groupData),
                      );
                      el.setAttribute(
                        "data-lightbox-group-index",
                        String(index),
                      );

                      document.body.appendChild(el);
                      el.click();
                      document.body.removeChild(el);
                    }}
                  />
                </CarouselItem>
              ))}
              {canScroll && <div className="shrink-0 w-6" />}
            </CarouselContent>
            <GalleryNavNext show={canScroll} />
          </Carousel>
        </section>
      </GridContainer>
    </div>
  );
};

export default VideoShortsGallery;
